import { useCallback } from 'react';

import type { ShareActionsOptions, ShareChannel } from '#src/app/shared/hooks/share';

import { useCreateNewShare } from './useCreateNewShare';
import { type QrShareSource, useRedirect } from './useRedirect';
import { useTrackPromotionShare } from './useTrackPromotionShare';

type UseOnShareSuccessResult = {
	onSuccess: NonNullable<ShareActionsOptions['onSuccess']>;
	onShareTapped: NonNullable<ShareActionsOptions['onBeforeShare']>;
	// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
	/** The same `useRedirect` instance's QR redirect, so a channel tap still supersedes a pending one. */
	redirectAfterQrShare: ReturnType<typeof useRedirect>['redirectAfterQrShare'];
	/** Cancels that instance's pending QR download redirect. */
	cancelQrRedirect: ReturnType<typeof useRedirect>['cancelQrRedirect'];
};

export type { QrShareSource };

export function useOnShareSuccess(): UseOnShareSuccessResult {
	const trackPromotionShare = useTrackPromotionShare();
	const createNewShare = useCreateNewShare();
	const { redirect, onShareTapped, redirectAfterQrShare, cancelQrRedirect } = useRedirect();

	return {
		onSuccess: useCallback(
			async (channel: ShareChannel) => {
				// awaiting to make sure it's done before navigating
				await Promise.all([trackPromotionShare(), createNewShare(channel)]);

				redirect(channel);
			},
			[trackPromotionShare, createNewShare, redirect],
		),
		onShareTapped,
		redirectAfterQrShare,
		cancelQrRedirect,
	};
}
